import { useCallback, useMemo } from 'react';
import { FieldMask } from '@weave/schema-gen-ts/dist/google/protobuf/field_mask.pb';
import {
  AIReceptionistLocationUserSettings,
  Setting,
  SettingType_Enum,
} from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import { cloneDeep } from 'lodash-es';
import { MessagingSettingsV1 } from '@frontend/api-messaging-settings';
import { getUser } from '@frontend/auth-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { AI_RECEPTIONIST_FIELD_MASK_PATH_PREFIX_BASE } from './constants';

const DEFAULT_AI_RECEPTIONIST_SETTINGS: AIReceptionistLocationUserSettings = {
  enableNeedsAttentionNotifications: false,
  enableSessionCompleteNotifications: false,
};

export type SETTING_WITH_AI_RECEPTIONIST_SETTING = Setting & {
  aiReceptionistLocationUserSettings: AIReceptionistLocationUserSettings;
};

export const useAIReceptionistSettings = () => {
  const { selectedLocationIds: globalSelectedLocationIds, selectedOrgId } = useAppScopeStore();
  const user = getUser();
  const userId = user?.userID ?? '';

  const queriesData = MessagingSettingsV1.Queries.useGetQueries(
    globalSelectedLocationIds.map((locationId) => ({
      req: {
        locationId,
        orgId: selectedOrgId,
        settingType: SettingType_Enum.AI_RECEPTIONIST_LOCATION_USER_SETTINGS,
        userId,
      },
      option: {
        enabled: !!selectedOrgId && !!locationId,
        retry: 0,
      },
    }))
  );

  const isLoading = useMemo(() => queriesData.some((q) => q.isLoading), [queriesData]);

  const getLocationSettings = useCallback(
    (locationId: string) =>
      queriesData.find((q) => q.data?.locationId === locationId)?.data?.setting as SETTING_WITH_AI_RECEPTIONIST_SETTING,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(queriesData)]
  );

  const getLocationAIReceptionistSettings = useCallback(
    (locationId: string) => {
      const locationSettings = getLocationSettings(locationId);
      return locationSettings?.aiReceptionistLocationUserSettings ?? DEFAULT_AI_RECEPTIONIST_SETTINGS;
    },
    [getLocationSettings]
  );

  const { mutate } = MessagingSettingsV1.Mutations.useSetMutation({ optimisticUpdate: true });

  const setAIReceptionistSetting = useCallback(
    async (
      locationId: string,
      newAIReceptionistSetting: Partial<AIReceptionistLocationUserSettings>,
      fieldMaskPaths: string[]
    ) => {
      const queryDataInstance = queriesData.find((query) => query.data?.locationId === locationId)?.data;
      if (!queryDataInstance) return;

      const aiReceptionistSettings = getLocationAIReceptionistSettings(locationId);

      const copy = {
        ...cloneDeep(queryDataInstance),
        locationId,
        modifiedBy: userId,
        orgId: selectedOrgId,
        mask: new FieldMask({ paths: fieldMaskPaths }),
        setting: {
          ...queryDataInstance?.setting,
          aiReceptionistLocationUserSettings: {
            ...aiReceptionistSettings,
            ...newAIReceptionistSetting,
          },
        },
        settingType: SettingType_Enum.AI_RECEPTIONIST_LOCATION_USER_SETTINGS,
      };

      mutate(copy);
    },
    [getLocationAIReceptionistSettings, mutate, queriesData, selectedOrgId, userId]
  );

  const setAIReceptionistSettingsByProperty = useCallback(
    (
      locationId: string,
      keyToChange: keyof AIReceptionistLocationUserSettings,
      value: AIReceptionistLocationUserSettings[typeof keyToChange]
    ) => {
      const aiReceptionistSettings = getLocationAIReceptionistSettings(locationId);

      setAIReceptionistSetting(
        locationId,
        {
          ...aiReceptionistSettings,
          [keyToChange]: value,
        },
        [AI_RECEPTIONIST_FIELD_MASK_PATH_PREFIX_BASE + keyToChange]
      );
    },
    [getLocationAIReceptionistSettings, setAIReceptionistSetting]
  );

  return {
    getLocationAIReceptionistSettings,
    isLoading,
    setAIReceptionistSetting,
    setAIReceptionistSettingsByProperty,
  };
};
